/**
 * Toothrocket clinical product catalog.
 * Single source of truth for both the public "Send a case" form and the
 * Admin "Add case" modal.
 *
 * needs:
 *   - "teeth"    → per-tooth selection on the tooth chart
 *   - "arch"     → upper / lower / both (no tooth-level selection)
 *   - "span"     → start + end tooth (continuous bridge / bar)
 *
 * Extra spec fields (material, design, frame, etc.) are declared per-product
 * so the form can render them dynamically.
 */
export const PRODUCT_CATEGORIES = [
    {
        id: "restorations",
        label: "Restorations",
        desc: "Crowns, veneers, inlays — the 10-year-warranty range.",
        accent: "from-cyan-50 to-white",
    },
    {
        id: "ortho_appliances",
        label: "Orthodontic & Appliances",
        desc: "Retainers, splints, nightguards, sports guards, braces.",
        accent: "from-teal-50 to-white",
    },
    {
        id: "surgical_implant",
        label: "Surgical & Implant",
        desc: "Surgical guides, implant bars and prosthetic frames.",
        accent: "from-slate-100 to-white",
    },
    {
        id: "bespoke",
        label: "Bespoke",
        desc: "Dental jewellery, snap-fit veneers, custom esthetics.",
        accent: "from-amber-50 to-white",
    },
];

export const PRODUCT_CATALOG = [
    // Restorations
    {
        slug: "permanent-crown",
        name: "Permanent Crown",
        category: "restorations",
        needs: "teeth",
        hint: "Full-contour Nano Ceramic Hybrid — single units.",
        shade: true,
        material_options: ["Nano Ceramic Hybrid", "Zirconia", "PFM"],
    },
    {
        slug: "same-day-crown",
        name: "Same-Day Crown",
        category: "restorations",
        needs: "teeth",
        hint: "Express single-visit crown · same-day dispatch.",
        shade: true,
    },
    {
        slug: "veneer",
        name: "Veneer",
        category: "restorations",
        needs: "teeth",
        hint: "0.3–0.5 mm Nano Hybrid laminate.",
        shade: true,
    },
    {
        slug: "inlay",
        name: "Inlay",
        category: "restorations",
        needs: "teeth",
        hint: "Class II cavity restoration — within cusps.",
        shade: true,
    },
    {
        slug: "onlay",
        name: "Onlay",
        category: "restorations",
        needs: "teeth",
        hint: "Cusp-covering restoration — conservative crown alternative.",
        shade: true,
    },
    {
        slug: "overlay",
        name: "Overlay",
        category: "restorations",
        needs: "teeth",
        hint: "Full-occlusal coverage, sides preserved.",
        shade: true,
    },
    {
        slug: "bridge",
        name: "Bridge",
        category: "restorations",
        needs: "span",
        hint: "Multi-unit fixed bridge — pick the abutments + pontics.",
        shade: true,
        material_options: ["Nano Ceramic Hybrid", "Zirconia", "PFM"],
    },

    // Orthodontic & Appliances
    {
        slug: "retainer",
        name: "Retainer",
        category: "ortho_appliances",
        needs: "arch",
        hint: "Post-orthodontic retention — clear or hard.",
        material_options: ["Clear thermoplastic", "Hawley wire", "Bonded fixed"],
    },
    {
        slug: "nightguard",
        name: "Nightguard",
        category: "ortho_appliances",
        needs: "arch",
        hint: "Bruxism protection — soft, dual-laminate, or hard.",
        material_options: ["Soft 2 mm", "Dual-laminate", "Hard acrylic"],
    },
    {
        slug: "sports-guard",
        name: "Sports Guard",
        category: "ortho_appliances",
        needs: "arch",
        hint: "Custom impact-tuned mouthguard — team colours optional.",
        material_options: ["Boxing", "Rugby", "Hockey", "MMA", "Generic"],
    },
    {
        slug: "braces-metal",
        name: "Braces — Metal",
        category: "ortho_appliances",
        needs: "arch",
        hint: "Stainless-steel brackets · per-arch fabrication.",
    },

    // Surgical & Implant
    {
        slug: "surgical-guide",
        name: "Surgical Guide",
        category: "surgical_implant",
        needs: "arch",
        hint: "Fully-guided implant placement guide.",
    },
    {
        slug: "implant-bar",
        name: "Metal Bar (Implant Bridge)",
        category: "surgical_implant",
        needs: "span",
        hint: "Cobalt-chrome or titanium bar supporting implant bridges.",
        material_options: ["Cobalt-chrome", "Titanium", "Zirconia"],
    },

    // Bespoke
    {
        slug: "veneer-clip-on",
        name: "Veneer Clip-On (Snap-Fit)",
        category: "bespoke",
        needs: "arch",
        hint: "Reversible smile makeover · zero enamel prep.",
        shade: true,
    },
    {
        slug: "dental-jewelry",
        name: "Dental Jewellery",
        category: "bespoke",
        needs: "teeth",
        hint: "Crystal / Swarovski / diamond inset on a printed mount.",
    },
];

export const PRODUCTS_BY_SLUG = Object.fromEntries(PRODUCT_CATALOG.map((p) => [p.slug, p]));

/** Group catalog by category for the dropdown UI. */
export function groupedProducts() {
    const out = PRODUCT_CATEGORIES.map((c) => ({
        ...c,
        items: PRODUCT_CATALOG.filter((p) => p.category === c.id),
    }));
    return out.filter((c) => c.items.length > 0);
}

export const SHADES = ["A1", "A2", "A3", "A3.5", "A4", "B1", "B2", "B3", "B4", "C1", "C2", "C3", "D2", "D3", "BL1", "BL2"];

// Vita 3D-Master shade guide (modern). The shade code combines lightness
// (0–5), chroma (M=1, L=1.5, R=2.5), and a chroma level (1, 2, 3).
// Source: Vita Zahnfabrik official 3D-Master shade map.
export const SHADES_3D_MASTER = [
    // Bleached (value 0)
    "0M1", "0M2", "0M3",
    // Value 1
    "1M1", "1M2",
    // Value 2
    "2L1.5", "2L2.5", "2M1", "2M2", "2M3", "2R1.5", "2R2.5",
    // Value 3
    "3L1.5", "3L2.5", "3M1", "3M2", "3M3", "3R1.5", "3R2.5",
    // Value 4
    "4L1.5", "4L2.5", "4M1", "4M2", "4M3", "4R1.5", "4R2.5",
    // Value 5
    "5M1", "5M2", "5M3",
];

/** Detect which Vita system a shade belongs to. Defaults to "classical"
 *  for unknowns so legacy data keeps rendering on the Classical tab.
 */
export function detectShadeSystem(shade) {
    if (!shade) return "classical";
    if (SHADES_3D_MASTER.includes(shade)) return "3d-master";
    if (SHADES.includes(shade)) return "classical";
    // Heuristic for free-form input — anything starting with a digit is 3D-Master.
    if (/^\d/.test(shade)) return "3d-master";
    return "classical";
}

export const ARCH_OPTIONS = [
    { id: "upper", label: "Upper arch" },
    { id: "lower", label: "Lower arch" },
    { id: "both",  label: "Both arches" },
];
